/**
 * useDealLocation — the caller's own ZIP + radius for Deals.
 *  • Guests: localStorage (`guest_zip`, `guest_radius`), pre-warmed via /warm-zip.
 *  • Subscribers: their profile record (`zip_code`, `deal_radius_mi`), saved via
 *    PUT /weekly-ads/zip-code. Onboarding/Settings ZIP becomes the default.
 */
import { useCallback, useEffect, useState } from "react";
import { useGuest } from "../contexts/GuestContext";
import { useAuth } from "../contexts/AuthContext";
import { guestHasAnyAlert, syncGuestAlertProfile } from "../lib/guestAlerts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ZIP_KEY = "guest_zip";
const RADIUS_KEY = "guest_radius";
const PROMPTED_KEY = "guest_zip_prompted";
export const RADIUS_OPTIONS = [5, 10, 25, 50];
export const DEFAULT_RADIUS = 10;

const readLocal = (key) => { try { return localStorage.getItem(key) || ""; } catch { return ""; } };

export function useDealLocation() {
  const { isGuest } = useGuest();
  const { user, patchUser } = useAuth();
  const [guestZip, setGuestZip] = useState(() => readLocal(ZIP_KEY));
  const [guestRadius, setGuestRadius] = useState(() => Number(readLocal(RADIUS_KEY)) || DEFAULT_RADIUS);
  const [promptOpen, setPromptOpen] = useState(false);

  const zip = isGuest ? guestZip : (user?.zip_code || "");
  const radius = isGuest ? guestRadius : (Number(user?.deal_radius_mi) || DEFAULT_RADIUS);

  // First-visit auto-prompt for guests
  useEffect(() => {
    if (!isGuest || guestZip) return;
    if (readLocal(PROMPTED_KEY) === "1") return;
    setPromptOpen(true);
  }, [isGuest, guestZip]);

  const setLocation = useCallback(async (nextZip, nextRadius = DEFAULT_RADIUS) => {
    const clean = (nextZip || "").replace(/[^0-9]/g, "").slice(0, 5);
    const r = RADIUS_OPTIONS.includes(Number(nextRadius)) ? Number(nextRadius) : DEFAULT_RADIUS;
    if (isGuest) {
      setGuestZip(clean);
      setGuestRadius(r);
      try {
        if (clean) localStorage.setItem(ZIP_KEY, clean); else localStorage.removeItem(ZIP_KEY);
        localStorage.setItem(RADIUS_KEY, String(r));
        localStorage.setItem(PROMPTED_KEY, "1");
        if (guestHasAnyAlert()) syncGuestAlertProfile().catch(() => {});
      } catch { /* ignore */ }
      if (clean) {
        fetch(`${BACKEND_URL}/api/weekly-ads/warm-zip`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ zip_code: clean, source: "guest_modal" }),
        }).catch(() => {});
      }
      return true;
    }
    const res = await fetch(`${BACKEND_URL}/api/weekly-ads/zip-code`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ zip_code: clean, radius_mi: r }),
    });
    if (!res.ok) return false;
    patchUser({ zip_code: clean, deal_radius_mi: r });
    return true;
  }, [isGuest, patchUser]);

  const dismissPrompt = useCallback(() => {
    try { localStorage.setItem(PROMPTED_KEY, "1"); } catch { /* ignore */ }
    setPromptOpen(false);
  }, []);

  const openPrompt = useCallback(() => setPromptOpen(true), []);

  return { isGuest, zip, radius, setLocation, promptOpen, setPromptOpen, dismissPrompt, openPrompt };
}
